/**
 * Trip display formatters for admin list and detail views.
 */

import { format } from "date-fns";
import { parseCorridorToProvinces } from "@/shared/utils/routeCorridorUtils.js";
import { formatCapacityClassLabel } from "./tripOperatorFields.js";

const PROVINCE_LABELS = {
  WC: "Western Cape",
  GP: "Gauteng",
  EC: "Eastern Cape",
  KZN: "KwaZulu-Natal",
  LP: "Limpopo",
  MP: "Mpumalanga",
  NW: "North West",
  NC: "Northern Cape",
  FS: "Free State",
};

export function formatProvinceLabel(code) {
  if (!code) return "";
  return PROVINCE_LABELS[String(code).toUpperCase()] || code;
}

export function formatRouteShort(corridor) {
  const provinces = parseCorridorToProvinces(corridor);
  if (provinces.length >= 2) {
    return provinces.join(" → ");
  }
  return corridor || "—";
}

export function formatRouteDescription(corridor) {
  const provinces = parseCorridorToProvinces(corridor);
  if (provinces.length >= 3) {
    const start = formatProvinceLabel(provinces[0]);
    const end = formatProvinceLabel(provinces[provinces.length - 1]);
    const via = provinces
      .slice(1, -1)
      .map(formatProvinceLabel)
      .join(", ");
    return `${start} → ${end} via ${via}`;
  }
  if (provinces.length === 2) {
    return `${formatProvinceLabel(provinces[0])} → ${formatProvinceLabel(provinces[1])}`;
  }
  return corridor || "—";
}

export function getRouteTimelinePoints(corridor) {
  const provinces = parseCorridorToProvinces(corridor);
  if (provinces.length === 0) {
    return { start: null, via: [], end: null };
  }
  if (provinces.length === 1) {
    return {
      start: formatProvinceLabel(provinces[0]),
      via: [],
      end: null,
    };
  }
  return {
    start: formatProvinceLabel(provinces[0]),
    via: provinces.slice(1, -1).map(formatProvinceLabel),
    end: formatProvinceLabel(provinces[provinces.length - 1]),
  };
}

export function getCapacityPercent(trip) {
  if (Number.isFinite(Number(trip?.capacityUsedPercent))) {
    return Math.min(Math.max(Number(trip.capacityUsedPercent), 0), 100);
  }
  if (Number.isFinite(Number(trip?.utilisationPercent))) {
    return Math.min(Math.max(Number(trip.utilisationPercent), 0), 100);
  }
  const used = Number(trip?.capacityUsed) || 0;
  const total = Number(trip?.capacityTotal) || 0;
  if (total > 0) {
    return Math.min(Math.round((used / total) * 100), 100);
  }
  return 0;
}

export function formatCapacityFraction(trip) {
  if (trip?.capacityFraction) return trip.capacityFraction;
  const used = Number(trip?.capacityUsed) || 0;
  const total = Number(trip?.capacityTotal) || 0;
  if (total > 0) {
    return `${used.toFixed(1)} / ${total.toFixed(1)} m³`;
  }
  const orderCount = trip?.orderCount ?? trip?.orders?.length ?? 0;
  return orderCount ? `${orderCount} orders` : "—";
}

/** Tonnage / size chip label for Trip Details (vs m³ utilisation). */
export function formatTripLoadSize(trip) {
  return formatCapacityClassLabel(trip?.capacityClass) || "—";
}

/** True only when the PUBTRIP row is live on the PWA (`published: true`). */
export function isPublishedOnPwa(publishedTrip) {
  return publishedTrip?.published === true;
}

export function formatTripDeparture(date, pattern = "EEE, dd MMM yyyy, HH:mm") {
  if (!date) return "Not scheduled";
  try {
    return format(new Date(date), pattern);
  } catch {
    return "Not scheduled";
  }
}

export function formatCurrencyZAR(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return "—";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatRiskStatusLabel(riskSummary) {
  if (!riskSummary?.total) return "On Track";
  if (riskSummary.high > 0) return "High Risk";
  if (riskSummary.medium > 0) return "Needs Attention";
  return "On Track";
}

export function getRiskStatusTone(riskSummary) {
  if (!riskSummary?.total) return "success";
  if (riskSummary.high > 0) return "danger";
  if (riskSummary.medium > 0) return "warning";
  return "success";
}

export function getVehicleLabel(trip) {
  if (trip?.vehicle?.licensePlate) return trip.vehicle.licensePlate;
  if (trip?.vehicleId) return trip.vehicleId;
  return "Not assigned";
}

export function getDriverLabel(trip) {
  if (trip?.driver?.name) return trip.driver.name;
  if (trip?.driverId) return trip.driverId;
  return "Not assigned";
}

export function getDriverPhone(trip) {
  return trip?.driver?.phone || trip?.driver?.mobile || null;
}

export function getTripProgressPercent(trip) {
  if (Number.isFinite(Number(trip?.progressPercent))) {
    return Math.min(Math.max(Number(trip.progressPercent), 0), 100);
  }
  const statusProgress = {
    planned: 0,
    loaded: 25,
    en_route: 60,
    completed: 100,
  };
  return statusProgress[trip?.status] ?? 0;
}

export function getOrderCount(trip) {
  return trip?.orderCount ?? trip?.ordersCount ?? trip?.orders?.length ?? 0;
}

export function getAvgCapacityPerOrder(trip) {
  const used = Number(trip?.capacityUsed) || 0;
  const count = getOrderCount(trip);
  if (!count || used <= 0) return null;
  return `${(used / count).toFixed(1)} m³`;
}

export function exportTripsToCsv(trips) {
  const headers = [
    "Trip ID",
    "Route",
    "Status",
    "Departure",
    "Vehicle",
    "Driver",
    "Capacity",
    "Progress %",
    "Orders",
  ];
  const rows = trips.map((trip) => [
    trip.routeCorridor || trip.id,
    formatRouteDescription(trip.routeCorridor),
    trip.status || "",
    formatTripDeparture(trip.departureDate),
    getVehicleLabel(trip),
    getDriverLabel(trip),
    formatCapacityFraction(trip),
    getTripProgressPercent(trip),
    getOrderCount(trip),
  ]);
  const csv = [headers, ...rows]
    .map((row) =>
      row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `trips-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
